import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RequestService from '../services/requestService';
import ProcessStepper from '../components/common/ProcessStepper';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  DataUsage as DataUsageIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';
import GoldCompatibilitySection from '../components/feasibility/GoldCompatibilitySection';


const steps = ['Anfrage prüfen', 'Datenschutz prüfen', 'Datenverfügbarkeit prüfen', 'Aufwand abschätzen', 'Ergebnis'];

const FeasibilityCheck = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // ID aus verschiedenen Quellen extrahieren
  const idFromParams = params.id;
  // Verbesserte Extraktion der ID aus dem Pfad
  let idFromPath = null;
  const pathParts = location.pathname.split('/');
  // Suche nach einer Zahl im Pfad
  for (let i = pathParts.length - 1; i >= 0; i--) {
    const part = pathParts[i];
    if (!isNaN(parseInt(part, 10))) {
      idFromPath = part;
      break;
    }
  }
  const idFromState = location.state?.requestId;
  
  // Debug-Ausgabe für URL-Parameter und Pfad
  console.log("URL-Parameter:", params);
  console.log("URL-Pfad:", location.pathname);
  console.log("ID aus Pfad extrahiert:", idFromPath);
  console.log("ID aus State:", idFromState);
  
  // Zusätzliches Debugging
  console.log("Vollständiger Pfad:", location.pathname);
  console.log("Pfadteile:", location.pathname.split('/'));
  console.log("Query-Parameter:", new URLSearchParams(location.search).toString());
  
  // ID für die Komponente festlegen
  let id = idFromParams;
  
  // Wenn keine ID in den Parametern ist, versuche sie aus dem State oder Pfad zu extrahieren
  if (!id) {
    if (idFromState) {
      id = idFromState;
    } else {
      // Prüfe, ob der letzte Teil des Pfads eine Zahl ist
      const pathId = parseInt(idFromPath, 10);
      if (!isNaN(pathId)) {
        id = idFromPath;
      }
    }
  }
  
  // Extrahiere die ID aus der URL, wenn sie im Format /feasibility-check/123 ist
  const feasibilityCheckIdMatch = location.pathname.match(/\/feasibility-check\/(\d+)/);
  if (!id && feasibilityCheckIdMatch && feasibilityCheckIdMatch[1]) {
    id = feasibilityCheckIdMatch[1];
    console.log("ID aus URL-Pfad /feasibility-check/ID extrahiert:", id);
  }
  
  // Extrahiere die ID aus der URL, wenn sie als Query-Parameter vorhanden ist
  const urlParams = new URLSearchParams(location.search);
  const queryId = urlParams.get('id');
  if (!id && queryId) {
    id = queryId;
    console.log("ID aus Query-Parameter extrahiert:", id);
  }
  
  // Fallback auf ID 2 für Testzwecke, wenn keine ID gefunden wurde
  if (!id) {
    id = '2';
    console.log("Keine ID gefunden, verwende Fallback-ID 2 für Testzwecke");
  }
  
  console.log("Verwendete ID:", id, "Typ:", typeof id);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [stepLoading, setStepLoading] = useState(false);
  const [patientCount, setPatientCount] = useState(null);
  const [variableAvailability, setVariableAvailability] = useState({});
  const [privacyTextModified, setPrivacyTextModified] = useState(false);
  const [ethicsTextModified, setEthicsTextModified] = useState(false);
  const [argusCheckRequested, setArgusCheckRequested] = useState(false);
  const [argusCheckCompleted, setArgusCheckCompleted] = useState(false);
  const [showArgusDialog, setShowArgusDialog] = useState(false);
  const [estimatedEffort, setEstimatedEffort] = useState({
    dataPreparation: '',
    analysis: '',
    reporting: ''
  });
  const [totalPrice, setTotalPrice] = useState('');
  const [notes, setNotes] = useState('');

  // Laden der Anfragedaten
  useEffect(() => {
    const loadRequest = async () => {
      try {
        // Bestimme die zu verwendende Request-ID
        let requestId;
        
        // Wenn eine ID aus der URL verfügbar ist, verwende diese
        if (id) {
          requestId = parseInt(id, 10);
          console.log("ID aus URL verfügbar:", requestId, typeof requestId);
          
          // Zusätzliche Prüfung, ob die ID gültig ist
          if (isNaN(requestId)) {
            console.error("ID konnte nicht in eine Zahl umgewandelt werden:", id);
            setLoading(false);
            setRequest(null);
            return;
          }
        } else {
          // Keine Fallback-ID mehr verwenden, stattdessen Fehler anzeigen
          console.error("Keine gültige ID in URL gefunden");
          setLoading(false);
          setRequest(null);
          return;
        }
        
        console.log("Verwende requestId:", requestId);
        
        // Verwende den neuen Endpunkt für Machbarkeitsprüfung
        console.log("Rufe getFeasibilityCheckData mit ID auf:", requestId);
        const requestData = await RequestService.getFeasibilityCheckData(requestId);
        console.log("Geladene Anfragedaten:", requestData);
        
        // Überprüfe, ob die geladenen Daten zur angeforderten ID passen
        if (requestData && requestData.id !== requestId) {
          console.error(`ID-Mismatch: Angefordert ${requestId}, erhalten ${requestData.id}`);
        }
        
        if (requestData) {
          console.log("Setze Anfrage:", requestData.title, "mit ID:", requestData.id);
          
          // Stelle sicher, dass alle benötigten Eigenschaften existieren
          if (!requestData.cohortDefinition) {
            requestData.cohortDefinition = [];
          }
          
          if (!requestData.requestedVariables) {
            requestData.requestedVariables = [];
          }
          
          if (!requestData.variableAvailability) {
            requestData.variableAvailability = {};
          }
          
          if (!requestData.feasibilityProgress && requestData.feasibilityProgress !== 0) {
            requestData.feasibilityProgress = requestData.status === 'feasibility' ? 60 : 0;
          }
          
          setRequest(requestData);
          
          // Wenn die Anfrage bereits im Machbarkeitsprüfungsstatus ist und Daten hat
          if (requestData.status === 'feasibility' && requestData.patientCount) {
            setPatientCount(requestData.patientCount);
            if (requestData.variableAvailability) {
              setVariableAvailability(requestData.variableAvailability);
            }
          }
          
          setLoading(false);
        } else {
          console.error("Keine Anfrage mit ID", requestId, "gefunden");
          setLoading(false);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Anfrage:", error);
        setLoading(false);
      }
    };
    
    loadRequest();
  }, [id]);

  const handleNext = () => {
    if (activeStep === 2) {
      // Simuliere Datenverfügbarkeitsprüfung
      setStepLoading(true);
      setTimeout(() => {
        // Wenn keine vorhandenen Daten, generiere Beispieldaten
        if (!patientCount) {
          setPatientCount(Math.floor(Math.random() * 200) + 50);
        }
        
        if (Object.keys(variableAvailability).length === 0 && request) {
          // Verwende die in der Anfrage definierten Verfügbarkeiten oder setze alle auf true
          if (request.variableAvailability) {
            setVariableAvailability(request.variableAvailability);
          } else {
            const availability = {};
            request.requestedVariables.forEach(variable => {
              availability[variable] = true; // Standardmäßig alle verfügbar
            });
            setVariableAvailability(availability);
          }
        }
        
        setStepLoading(false);
        setActiveStep(prevActiveStep => prevActiveStep + 1);
      }, 2000);
    } else {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleEffortChange = (field, value) => {
    setEstimatedEffort({
      ...estimatedEffort,
      [field]: value
    });
  };

  const getStepContent = (step) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (!request) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Anfrage konnte nicht geladen werden
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/requests')}
            sx={{ mt: 2 }}
          >
            Zurück zur Anfragenübersicht
          </Button>
        </Box>
      );
    }
    
    console.log("Rendering step content for request:", request);
    
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Anfrageinformationen" />
                <Divider />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {request.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Unternehmen:</strong> {request.company}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Eingangsdatum:</strong> {format(new Date(request.date.split('.').reverse().join('-')), 'dd. MMM yyyy', { locale: de })}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Beschreibung:</strong> {request.description}
                  </Typography>
                  
                  {/* Zeitraum der Machbarkeitsprüfung wird bereits kompakt im Header angezeigt */}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Kohortendefinition" />
                <Divider />
                <CardContent>
                  <List>
                    {request.cohortDefinition.map((condition, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <InfoIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={`${condition.field} ${condition.operator} ${condition.value}`} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Angeforderte Variablen" />
                <Divider />
                <CardContent>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {request.requestedVariables.map((variable, index) => (
                      <Chip key={index} label={variable} />
                    ))}
                  </Box>
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    <strong>Analysetyp:</strong> {request.analysisType}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Datenschutz und Ethik" />
                  <Divider />
                  <CardContent>
                    <Typography variant="body1" paragraph>
                      <strong>Anfrage:</strong> {request.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Unternehmen:</strong> {request.company}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Datenschutzkonzept
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Bitte prüfen Sie das vorausgefüllte Datenschutzkonzept und passen Sie es bei Bedarf an.
                  </Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    variant="outlined"
                    defaultValue={
                      `Datenschutzkonzept für Anfrage "${request?.title || 'Unbekannte Anfrage'}"\n\n` +
                      `1. Zweck der Datenverarbeitung: Durchführung einer Machbarkeitsanalyse für die Anfrage "${request?.title || 'Unbekannte Anfrage'}" von ${request?.company || 'Unbekanntes Unternehmen'}.\n\n` +
                      `2. Rechtsgrundlage: Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse) sowie § 27 BDSG (Forschungszwecke).\n\n` +
                      `3. Pseudonymisierung: Alle Patientendaten werden vor der Analyse pseudonymisiert. Eine Re-Identifikation ist nur durch autorisiertes Personal möglich.\n\n` +
                      `4. Datensicherheit: Die Daten werden ausschließlich auf gesicherten Servern innerhalb der EU verarbeitet und nach Abschluss der Analyse gemäß den gesetzlichen Aufbewahrungsfristen gelöscht.\n\n` +
                      `5. Betroffenenrechte: Die Rechte der betroffenen Personen gemäß Art. 15-21 DSGVO werden gewahrt.`
                    }
                    onChange={(e) => {
                      const defaultValue = `Datenschutzkonzept für Anfrage "${request?.title || 'Unbekannte Anfrage'}"\n\n` +
                      `1. Zweck der Datenverarbeitung: Durchführung einer Machbarkeitsanalyse für die Anfrage "${request?.title || 'Unbekannte Anfrage'}" von ${request?.company || 'Unbekanntes Unternehmen'}.\n\n` +
                      `2. Rechtsgrundlage: Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse) sowie § 27 BDSG (Forschungszwecke).\n\n` +
                      `3. Pseudonymisierung: Alle Patientendaten werden vor der Analyse pseudonymisiert. Eine Re-Identifikation ist nur durch autorisiertes Personal möglich.\n\n` +
                      `4. Datensicherheit: Die Daten werden ausschließlich auf gesicherten Servern innerhalb der EU verarbeitet und nach Abschluss der Analyse gemäß den gesetzlichen Aufbewahrungsfristen gelöscht.\n\n` +
                      `5. Betroffenenrechte: Die Rechte der betroffenen Personen gemäß Art. 15-21 DSGVO werden gewahrt.`;
                      
                      if (e.target.value !== defaultValue) {
                        setPrivacyTextModified(true);
                      } else {
                        setPrivacyTextModified(false);
                      }
                    }}
                    sx={{ mb: 3, backgroundColor: 'background.paper' }}
                  />
                  
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Dokumente hochladen
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Laden Sie relevante Dokumente zum Datenschutzkonzept hoch (z.B. Datenschutzfolgenabschätzung, Einwilligungserklärungen).
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadFileIcon />}
                    >
                      Dokumente hochladen
                      <input
                        type="file"
                        multiple
                        hidden
                      />
                    </Button>
                  </Box>
                  
                  {(privacyTextModified || ethicsTextModified) && !argusCheckRequested && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Achtung: Sie haben das Datenschutzkonzept oder Ethikvotum geändert. Eine technische Machbarkeitsprüfung durch ARGUS ist erforderlich, bevor Sie fortfahren können.
                    </Alert>
                  )}
                  
                  {argusCheckRequested && !argusCheckCompleted && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Die technische Machbarkeitsprüfung durch ARGUS wurde angefordert und wird bearbeitet.
                    </Alert>
                  )}
                  
                  {argusCheckCompleted && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Die technische Machbarkeitsprüfung durch ARGUS wurde erfolgreich abgeschlossen. Sie können nun fortfahren.
                    </Alert>
                  )}
                  
                  {(privacyTextModified || ethicsTextModified) && !argusCheckRequested && (
                    <Button 
                      variant="contained" 
                      color="primary" 
                      sx={{ mb: 2 }}
                      onClick={() => {
                        setShowArgusDialog(true);
                      }}
                    >
                      ARGUS-Prüfung anfordern
                    </Button>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Ethikvotum / Waiver
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Bitte prüfen Sie die Zusammenfassung des Ethikvotums oder Waivers und passen Sie sie bei Bedarf an.
                  </Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    defaultValue={
                      `Ethikvotum für Anfrage "${request?.title || 'Unbekannte Anfrage'}"\n\n` +
                      `Die Ethikkommission hat die Anfrage geprüft und keine ethischen Bedenken festgestellt. Ein formales Ethikvotum ist für diese Art der retrospektiven Datenanalyse nicht erforderlich (Waiver).\n\n` +
                      `Begründung: Bei der Anfrage handelt es sich um eine retrospektive Analyse bereits vorhandener Daten ohne zusätzliche Intervention oder Datenerhebung. Die Daten werden in pseudonymisierter Form verarbeitet.`
                    }
                    onChange={(e) => {
                      const defaultValue = `Ethikvotum für Anfrage "${request?.title || 'Unbekannte Anfrage'}"\n\n` +
                      `Die Ethikkommission hat die Anfrage geprüft und keine ethischen Bedenken festgestellt. Ein formales Ethikvotum ist für diese Art der retrospektiven Datenanalyse nicht erforderlich (Waiver).\n\n` +
                      `Begründung: Bei der Anfrage handelt es sich um eine retrospektive Analyse bereits vorhandener Daten ohne zusätzliche Intervention oder Datenerhebung. Die Daten werden in pseudonymisierter Form verarbeitet.`;
                      
                      if (e.target.value !== defaultValue) {
                        setEthicsTextModified(true);
                      } else {
                        setEthicsTextModified(false);
                      }
                    }}
                    sx={{ mb: 3, backgroundColor: 'background.paper' }}
                  />
                  
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Status des Ethikvotums</FormLabel>
                    <RadioGroup row defaultValue="waiver">
                      <FormControlLabel value="waiver" control={<Radio />} label="Waiver (nicht erforderlich)" />
                      <FormControlLabel value="pending" control={<Radio />} label="Beantragt" />
                      <FormControlLabel value="approved" control={<Radio />} label="Genehmigt" />
                    </RadioGroup>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );
        
      case 4:
        return (
          <Box>
            {stepLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Datenverfügbarkeit wird geprüft...
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body1">
                      Klicken Sie auf "Weiter", um die Datenverfügbarkeit zu prüfen.
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <CardHeader title="Anfrageinformationen" />
                    <Divider />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {request.title}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>Unternehmen:</strong> {request.company}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>Beschreibung:</strong> {request.description}
                      </Typography>
                      <Typography variant="body1" paragraph>
                        <strong>Analysetyp:</strong> {request.analysisType || "Nicht angegeben"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body1">
                  <strong>Gefundene Patienten:</strong> {patientCount || request.patientCount || "Wird berechnet..."}
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Variablenverfügbarkeit" />
                <Divider />
                <CardContent>
                  {request.requestedVariables && request.requestedVariables.length > 0 ? (
                    <List>
                      {request.requestedVariables.map((variable, index) => {
                        // Prüfe, ob die Variable in variableAvailability existiert oder in request.variableAvailability
                        const isAvailable = 
                          (Object.keys(variableAvailability).length > 0 && variableAvailability[variable] !== undefined) 
                            ? variableAvailability[variable] 
                            : (request.variableAvailability && request.variableAvailability[variable] !== undefined)
                              ? request.variableAvailability[variable]
                              : true; // Fallback: Variable ist verfügbar
                        
                        return (
                          <ListItem key={index}>
                            <ListItemIcon>
                              {isAvailable ? (
                                <CheckCircleIcon color="success" />
                              ) : (
                                <ErrorIcon color="error" />
                              )}
                            </ListItemIcon>
                            <ListItemText 
                              primary={variable} 
                              secondary={isAvailable ? 'Verfügbar' : 'Nicht verfügbar'} 
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      Keine Variablen angefordert.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Aufwandsabschätzung" />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Aufwand Datenvorbereitung</InputLabel>
                        <Select
                          value={estimatedEffort.dataPreparation}
                          onChange={(e) => handleEffortChange('dataPreparation', e.target.value)}
                          label="Aufwand Datenvorbereitung"
                        >
                          <MenuItem value="1_tag">1 Tag</MenuItem>
                          <MenuItem value="1_woche">1 Woche</MenuItem>
                          <MenuItem value="2_wochen">2 Wochen</MenuItem>
                          <MenuItem value="mehr_als_1_monat">Mehr als 1 Monat</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Aufwand Analyse</InputLabel>
                        <Select
                          value={estimatedEffort.analysis}
                          onChange={(e) => handleEffortChange('analysis', e.target.value)}
                          label="Aufwand Analyse"
                        >
                          <MenuItem value="1_tag">1 Tag</MenuItem>
                          <MenuItem value="1_woche">1 Woche</MenuItem>
                          <MenuItem value="2_wochen">2 Wochen</MenuItem>
                          <MenuItem value="mehr_als_1_monat">Mehr als 1 Monat</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Aufwand Berichterstattung</InputLabel>
                        <Select
                          value={estimatedEffort.reporting}
                          onChange={(e) => handleEffortChange('reporting', e.target.value)}
                          label="Aufwand Berichterstattung"
                        >
                          <MenuItem value="1_tag">1 Tag</MenuItem>
                          <MenuItem value="1_woche">1 Woche</MenuItem>
                          <MenuItem value="2_wochen">2 Wochen</MenuItem>
                          <MenuItem value="mehr_als_1_monat">Mehr als 1 Monat</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Preisvorschlag (€)"
                        type="number"
                        value={totalPrice}
                        onChange={(e) => setTotalPrice(e.target.value)}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Anmerkungen"
                        multiline
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body1">
                  Die Machbarkeitsprüfung wurde erfolgreich abgeschlossen.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Zusammenfassung" />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <DataUsageIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          {patientCount} Patienten
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        entsprechen den Kriterien
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <StorageIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          {Object.values(variableAvailability).filter(v => v).length} von {Object.values(variableAvailability).length} Variablen
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        sind verfügbar
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          {totalPrice} €
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Preisvorschlag
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body1" paragraph>
                    <strong>Aufwand Datenvorbereitung:</strong> {estimatedEffort.dataPreparation ? estimatedEffort.dataPreparation.replace(/_/g, ' ').replace('tag', 'Tag').replace('woche', 'Woche').replace('wochen', 'Wochen').replace('mehr als 1 monat', 'Mehr als 1 Monat') : ''}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Aufwand Analyse:</strong> {estimatedEffort.analysis ? estimatedEffort.analysis.replace(/_/g, ' ').replace('tag', 'Tag').replace('woche', 'Woche').replace('wochen', 'Wochen').replace('mehr als 1 monat', 'Mehr als 1 Monat') : ''}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Aufwand Berichterstattung:</strong> {estimatedEffort.reporting ? estimatedEffort.reporting.replace(/_/g, ' ').replace('tag', 'Tag').replace('woche', 'Woche').replace('wochen', 'Wochen').replace('mehr als 1 monat', 'Mehr als 1 Monat') : ''}
                  </Typography>
                  {notes && (
                    <Typography variant="body1" paragraph>
                      <strong>Anmerkungen:</strong> {notes}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      default:
        return 'Unbekannter Schritt';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!request) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Die angeforderte Anfrage konnte nicht gefunden werden.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/incoming-requests')}>
            Zurück zur Übersicht
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Paper elevation={2} sx={{ p: 0, pt: 0, mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <ProcessStepper 
            activeStep={1} 
            title="Bearbeitungsstatus der Anfrage" 
            requestStatus={request.status} 
            requestId={request.id}
          />
          <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
            <Typography variant="overline" display="block" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 0.5 }}>
              MACHBARKEITSPRÜFUNG
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {request.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              Anfrage von {request.company} • Eingangsdatum: {format(new Date(request.date.split('.').reverse().join('-')), 'dd. MMM yyyy', { locale: de })}
              {request.statusHistory && request.statusHistory.feasibility && (
                <Chip 
                  size="small" 
                  label={`Bearbeitungszeitraum: ${format(new Date(request.statusHistory.feasibility.startDate.split('.').reverse().join('-')), 'dd. MMM')} - ${request.statusHistory.feasibility.endDate ? format(new Date(request.statusHistory.feasibility.endDate.split('.').reverse().join('-')), 'dd. MMM yyyy') : 'heute'}`} 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2, height: 24, fontSize: '0.75rem' }}
                />
              )}
            </Typography>
          </Box>
        </Paper>

        {/* GOLD Compatibility Section - Feature 3 */}
        <GoldCompatibilitySection requestId={request.id} requestData={request} />

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/incoming-requests')}
            >
              Zurück zur Übersicht
            </Button>
            
            <Box>
              {activeStep !== 0 && (
                <Button
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                  disabled={loading}
                >
                  Zurück
                </Button>
              )}
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={() => {
                    navigate(`/negotiation?id=${request.id}`);
                  }}
                >
                  Zur Verhandlung
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading || (activeStep === 1 && (privacyTextModified || ethicsTextModified) && !argusCheckCompleted)}
                >
                  {activeStep === steps.length - 2 ? 'Abschließen' : 'Weiter'}
                </Button>
              )}
            </Box>
          </Box>

          {/* Dialog für ARGUS-Prüfung */}
          <Dialog
            open={showArgusDialog}
            onClose={() => setShowArgusDialog(false)}
            aria-labelledby="argus-dialog-title"
            aria-describedby="argus-dialog-description"
          >
            <DialogTitle id="argus-dialog-title">
              ARGUS-Prüfung anfordern
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="argus-dialog-description">
                Sie haben Änderungen am Datenschutzkonzept oder Ethikvotum vorgenommen. 
                ARGUS muss prüfen, ob diese Änderungen technisch umsetzbar sind.
                <br /><br />
                Falls technische Anpassungen am Code erforderlich sind, kann dies zu zusätzlichen Kosten führen. 
                In diesem Fall erhalten Sie ein Angebot in Form eines Dienstleistungsauftrags.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowArgusDialog(false)} color="primary">
                Abbrechen
              </Button>
              <Button 
                onClick={() => {
                  setShowArgusDialog(false);
                  setArgusCheckRequested(true);
                  
                  // Simuliere die ARGUS-Prüfung (in einer realen Anwendung würde hier ein API-Aufruf erfolgen)
                  setTimeout(() => {
                    setArgusCheckCompleted(true);
                  }, 3000);
                }} 
                variant="contained" 
                color="primary"
              >
                ARGUS-Prüfung starten
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default FeasibilityCheck;
